import React, { useState } from 'react';

interface WiziQuizInstructionsModalProps {
  show: boolean;
  quizName: string;
  timeLimit: number;
  totalQuestions: number;
  onProceed: () => void;
  onCancel: () => void;
}

const WiziQuizInstructionsModal: React.FC<WiziQuizInstructionsModalProps> = ({
  show,
  quizName,
  timeLimit,
  totalQuestions,
  onProceed,
  onCancel,
}) => {
  const [agreed, setAgreed] = useState(false);

  if (!show) return null;

  const handleProceed = () => {
    if (agreed) {
      onProceed();
    } else {
      alert('Please read and accept the instructions before proceeding.');
    }
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-info-circle me-2"></i>
              GENERAL INSTRUCTIONS
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onCancel}
            ></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-info">
              <strong>Please read the instructions carefully</strong>
            </div>

            {/* General Instructions */}
            <div className="mb-4">
              <h6 className="text-primary fw-bold">General Instructions:</h6>
              <ol>
                <li className="mb-2">
                  <strong>Total duration of {quizName}</strong> is {timeLimit} minutes.
                </li>
                <li className="mb-2">
                  The quiz will be set off at the server. The countdown timer at the top right corner of screen will display the
                  remaining time available for you to complete the examination. When the timer reaches zero, the examination will end
                  by itself. You will not be required to end or submit your examination.
                </li>
                <li className="mb-2">
                  The Questions Palette displayed on the right side of screen will show the status of each question using one of the
                  following symbols:
                  <div className="mt-2">
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-secondary me-2" style={{ width: '30px' }}>1</span>
                      <span>You have not visited the question yet.</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-danger me-2" style={{ width: '30px' }}>1</span>
                      <span>You have not answered the question.</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-success me-2" style={{ width: '30px' }}>1</span>
                      <span>You have answered the question.</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-warning me-2" style={{ width: '30px' }}>1</span>
                      <span>You have NOT answered the question, but have marked the question for review.</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-info me-2" style={{ width: '30px' }}>1</span>
                      <span>The question(s) "Answered and Marked for Review" will be considered for evaluation.</span>
                    </div>
                  </div>
                </li>
                <li className="mb-2">
                  You can click on the <strong>"→"</strong> arrow which appears to the left of question palette to collapse the
                  question palette thereby maximizing the question window. To view the question palette again, you can click on{' '}
                  <strong>"←"</strong> which appears on the right side of question window.
                </li>
                <li className="mb-2">
                  You can click on the <strong>"Profile"</strong> image on the right corner of your screen to change the display
                  language during the exam for entire question paper. On clicking of Profile image you will get a drop-down to change
                  the question content to the desired language.
                </li>
                <li className="mb-2">
                  You can click on <i className="fas fa-arrow-down"></i> to navigate to the bottom of the question box, without
                  scrolling.
                </li>
              </ol>
            </div>

            {/* Navigating to a Question */}
            <div className="mb-4">
              <h6 className="text-primary fw-bold">Navigating to a Question:</h6>
              <ol start={7}>
                <li className="mb-2">To answer a question, do the following:</li>
                <ol type="a">
                  <li className="mb-2">
                    Click on the question number in the Question Palette at the right of your screen to go to that numbered question
                    directly. Note that using this option does NOT save your answer to the current question.
                  </li>
                  <li className="mb-2">
                    Click on <strong>Save & Next</strong> to save your answer for the current question and then go to the next
                    question.
                  </li>
                  <li className="mb-2">
                    Click on <strong>Mark for Review & Next</strong> to save your answer for the current question, mark it for
                    review, and then go to the next question.
                  </li>
                </ol>
              </ol>
            </div>

            {/* Answering a Question */}
            <div className="mb-4">
              <h6 className="text-primary fw-bold">Answering a Question:</h6>
              <ol start={8}>
                <li className="mb-2">Procedure for answering a multiple choice type question:</li>
                <ol type="a">
                  <li className="mb-2">
                    To select your answer, click on the button of one of the options or click on the{' '}
                    <strong>Clear Response</strong> button to deselect your chosen answer.
                  </li>
                  <li className="mb-2">
                    To change your chosen answer, click on the button of another option again or click on the{' '}
                    <strong>Clear Response</strong> button to deselect your chosen answer and then click on the button of another
                    option.
                  </li>
                  <li className="mb-2">
                    To save your answer, you MUST click on the <strong>Save & Next</strong> button.
                  </li>
                  <li className="mb-2">
                    To mark the question for review, click on the <strong>Mark for Review & Next</strong> button.
                  </li>
                </ol>
                <li className="mb-2">
                  To change your answer to a question that has already been answered, first select that question for answering and
                  then follow the procedure for answering that type of question.
                </li>
              </ol>
            </div>

            {/* Navigating through sections */}
            <div className="mb-4">
              <h6 className="text-primary fw-bold">Navigating through sections:</h6>
              <ol start={10}>
                <li className="mb-2">
                  Sections in this question paper are displayed on the top bar of the screen. Questions in a section can be viewed by
                  clicking on the section name. The section you are currently viewing is highlighted.
                </li>
                <li className="mb-2">
                  After clicking the <strong>Save & Next</strong> button on the last question for a section, you will automatically be
                  taken to the first question of the next section.
                </li>
                <li className="mb-2">
                  You can shuffle between sections and questions anytime during the examination as per your convenience only during
                  the time stipulated.
                </li>
                <li className="mb-2">
                  Candidate can view the corresponding section summary as part of the legend that appears in every section above the
                  question palette.
                </li>
              </ol>
            </div>

            <div className="alert alert-warning">
              <small>
                <strong>Please note:</strong> All questions will appear in your default language. This language can be changed for a
                particular question later on.
              </small>
            </div>

            {/* Agreement Checkbox */}
            <div className="form-check mt-4 p-3 border rounded bg-light">
              <input
                className="form-check-input"
                type="checkbox"
                id="agreeInstructions"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="agreeInstructions">
                <small>
                  I have read and understood the instructions. All computer hardware allotted to me are in proper working condition. I
                  declare that I am not in possession of / not carrying any prohibited gadget like mobile phone, bluetooth devices
                  etc. /any prohibited material with me into the Examination Hall. I agree that in case of not adhering to the
                  instructions, I shall be liable to be debarred from this Test and/or to disciplinary action, which may include ban
                  from future Tests / Examinations.
                </small>
              </label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success btn-lg px-5"
              onClick={handleProceed}
              disabled={!agreed}
            >
              <i className="fas fa-check-circle me-2"></i>
              PROCEED
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WiziQuizInstructionsModal;
